import React from 'react';
import { connect } from 'react-redux';
import { IntlShape, injectIntl } from 'react-intl';

import {
  getWorkItOutData, getPickAPlanData, getIsCharity, getChosenPlanData, getPromoCodeDiscount,
} from 'store/modules/sign-up/selectors';
import { IReducerState } from 'store/main-reducer';
import {
  IWorkItOutData, IPickAPlanData, IPlan, PromoCodeDiscount, IExtra,
} from 'types';
import { getExtras } from 'store/modules/options/selectors';
import { calculateSmartHrCost } from 'tools/utilities/smart-hr-cost';
import {
  MONTHLY_BASE_FEE, PRICE_PER_EMPLOYEE, IS_CHARITY_DISCOUNT_AMOUNT, BASE_SETUP_FEE,
} from 'store/modules/app/constants';
import { PricingView, LabelAndAmount } from './Pricing';
import { PricingPlaceholder } from './PricingPlaceholder';

interface ValueProps {
  extrasOptions: IExtra[];
  workItOutData: IWorkItOutData;
  pickAPlanData: IPickAPlanData;
  chosenPlan: IPlan | undefined;
  isCharity: boolean;
  promoCodeDiscount?: PromoCodeDiscount;
}

interface Props extends ValueProps {
  intl: IntlShape;
  isSmall?: boolean;
}

const PricingController = (props: Props) => {
  const {
    intl,
    workItOutData,
    pickAPlanData,
    isCharity,
    promoCodeDiscount,
    chosenPlan,
    extrasOptions,
    isSmall,
  } = props;

  const monthlyBaseFee = MONTHLY_BASE_FEE;
  const pricePerEmployee = PRICE_PER_EMPLOYEE;

  if (!chosenPlan) {
    return <PricingPlaceholder />;
  }

  // =========================
  // Employee payments breakdown section
  // =========================

  let totalEmployeeCount = 0;

  const employeePayments: LabelAndAmount[] = workItOutData.payRuns.reduce<LabelAndAmount[]>((accumulator, { payCycle, employeeCount }) => {
    if (!payCycle || !employeeCount) {
      return accumulator;
    }

    let employeeCountAdjustedToMonthly = 0;

    switch (payCycle) {
      case 'Weekly':
        employeeCountAdjustedToMonthly = (employeeCount * 52) / 12;
        break;
      case 'Fortnightly':
        employeeCountAdjustedToMonthly = (employeeCount * 26) / 12;
        break;

      default:
        employeeCountAdjustedToMonthly = employeeCount;
        break;
    }

    totalEmployeeCount += employeeCount || 0;

    const totalCost = employeeCountAdjustedToMonthly * pricePerEmployee;
    // const label = `${payCycle} pay run for ${employeeCount}`;
    const label = `${payCycle} ${intl.formatMessage({ id: 'PAY_RUN_FOR' })} ${employeeCount}*`;

    return [
      ...accumulator,
      {
        label,
        amount: totalCost,
        id: payCycle,
      },
    ];
  }, []);

  // =========================
  // Extras section
  // =========================
  const extras = pickAPlanData.extras.reduce<LabelAndAmount[]>((accumulator, extraId) => {
    const extra = extrasOptions.find(({ id }) => id === extraId);

    if (!extra) {
      return accumulator;
    }

    const label = extra.title;
    const totalCost = calculateSmartHrCost(totalEmployeeCount);

    return [
      ...accumulator,
      {
        label,
        amount: totalCost,
        id: extraId,
      },
    ];
  }, []);

  // =========================
  // Discounts section
  // =========================
  const hasPromoCodeDiscount = !!promoCodeDiscount;
  const discounts: LabelAndAmount[] = [];
  let monthlyDiscountValue = 0;

  // Don't apply the charity discount if a promoCodeDiscount is already applied
  if (!hasPromoCodeDiscount) {
    if (isCharity) {
      discounts.push({
        id: 'charity-discount',
        label: intl.formatMessage({ id: 'CHARITY_DISCOUNT' }),
        amount: IS_CHARITY_DISCOUNT_AMOUNT,
      });

      monthlyDiscountValue += IS_CHARITY_DISCOUNT_AMOUNT;
    }
  }


  // Add base setup fee
  let baseSetupFeeLabel = intl.formatMessage({ id: 'SETUP_FEE' });
  let setupFeeDiscount = 0;

  // =========================
  // Promo code section
  // =========================
  // Only the following discount values have been accounted for - all other discounts will be shown in the blue Pricing-feature
  // - monthlyDiscount

  if (promoCodeDiscount) {
    const {
      code,
      monthlyDiscount,
      baseSetupFeeDiscount,
    } = promoCodeDiscount;

    if (monthlyDiscount) {
      monthlyDiscountValue += monthlyDiscount;

      discounts.push({
        id: code,
        label: intl.formatMessage({ id: 'MONTHLY_DISCOUNT' }),
        amount: monthlyDiscount,
      });
    }

    if (baseSetupFeeDiscount) {
      baseSetupFeeLabel += ` ${intl.formatMessage({ id: 'DISCOUNT' })} ($-${baseSetupFeeDiscount.toFixed(2)})`;
      setupFeeDiscount += baseSetupFeeDiscount;
    }
  }

  // =========================
  // Setup fees, don't effect total amount as these are one off payments
  // But discount codes can effect the setup fees
  // =========================
  const setupFees: LabelAndAmount[] = [];
  let totalBaseSetupFeeAmount = BASE_SETUP_FEE - setupFeeDiscount;

  // Base setup fee cannot be negative
  if (totalBaseSetupFeeAmount < 0) {
    totalBaseSetupFeeAmount = 0;
  }

  setupFees.push({
    id: 'base-setup-fee',
    label: baseSetupFeeLabel,
    amount: totalBaseSetupFeeAmount,
  });

  // =========================
  // Total amount section
  // =========================
  const totalEmployeePayment = employeePayments.reduce((accumulator, { amount }) => accumulator + amount, 0);
  const totalExtras = extras.reduce((accumulator, { amount }) => accumulator + amount, 0);

  // Monthly fee cannot be negative
  let totalMonthlyFee = monthlyBaseFee - monthlyDiscountValue;

  if (totalMonthlyFee < 0) {
    totalMonthlyFee = 0;
  }

  const totalAmount = totalMonthlyFee + totalExtras + totalEmployeePayment;

  const totalAmountLabel = `$${totalAmount.toFixed(2)}/mo`;

  return (
    <PricingView
      pricePerEmployee={pricePerEmployee}
      monthlyBaseFee={monthlyBaseFee}
      employeePayments={employeePayments}
      chosenPlan={chosenPlan}
      extras={extras}
      setupFees={setupFees}
      discounts={discounts}
      totalAmountLabel={totalAmountLabel}

      promoCodeDiscount={promoCodeDiscount}
      isSmall={isSmall}
    />
  );
};

const mapStateToProps = (state: IReducerState): ValueProps => ({
  workItOutData: getWorkItOutData(state),
  pickAPlanData: getPickAPlanData(state),
  isCharity: getIsCharity(state),
  promoCodeDiscount: getPromoCodeDiscount(state),
  chosenPlan: getChosenPlanData(state),
  extrasOptions: getExtras(state),
});

export const Pricing = injectIntl(connect(
  mapStateToProps,
)(PricingController));
